import { useState, useEffect, useRef } from "react";
import ContactService from "../../../services/api/contact.service";
import {
  MessageSquare,
  Mail,
  Clock,
  Plus,
  Cat,
  Send,
  User,
  Headset,
} from "lucide-react";
import { toast } from "react-toastify";

const UserSupport = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");
  const replyTextareaRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const response = await ContactService.getUserSupport();
        setContacts(response.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchContacts();
  }, []);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [selectedContact?.replies]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const handleSubmitReply = async () => {
    if (!selectedContact || !replyMessage.trim()) {
      toast.error("Please enter a reply message");
      return;
    }

    try {
      const response = await ContactService.submitUserSupportReply(
        selectedContact._id,
        {
          message: replyMessage,
          subject: `Re: ${selectedContact.subject}`,
        }
      );

      // Update local state
      setSelectedContact(response.data);
      setContacts(
        contacts.map((c) => (c._id === selectedContact._id ? response.data : c))
      );

      // Clear reply textarea
      setReplyMessage("");
      toast.success("Reply sent successfully");
    } catch (error) {
      if (error.response && error.response.data) {
        // Handle validation errors
        if (
          error.response.data.errors &&
          Array.isArray(error.response.data.errors)
        ) {
          error.response.data.errors.forEach((err) => {
            toast.error(err);
          });
        } else if (error.response.data.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error("Failed to send reply");
        }
      } else {
        toast.error("Failed to send reply");
      }
    }
  };

  const handleCreateNewTicket = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
    const subject = formData.get("subject");
    const message = formData.get("message");

    if (!subject || !message) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      const response = await ContactService.createSupportMessage({
        subject,
        message,
      });

      // Use the response data directly as it now matches our needs
      const newContact = {
        ...response,
        message: response.replies[0].message, // Ensure message is set from first reply
      };

      setContacts([newContact, ...contacts]);
      setSelectedContact(newContact);
      setNewTicketOpen(false);
      toast.success("Message created successfully");
    } catch (error) {
      if (error.response && error.response.data) {
        // Handle validation errors
        if (
          error.response.data.errors &&
          Array.isArray(error.response.data.errors)
        ) {
          error.response.data.errors.forEach((err) => {
            toast.error(err);
          });
        } else if (error.response.data.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error(error.message || "Failed to create message");
        }
      } else {
        toast.error(error.message || "Failed to create message");
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-bounce">
            <Headset size={32} className="text-blue-400" />
          </div>
          <p className="mt-4 text-gray-600">Loading your support Message...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <Headset size={20} className="text-red-500" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">Error: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="flex items-center mb-6">
        <Headset size={28} className="text-blue-500 mr-2" />
        <h1 className="text-2xl font-bold">Support Center</h1>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Left sidebar - ticket list */}
        <div className="md:w-1/3 bg-white rounded-lg shadow-md p-4 border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Your Message</h2>
            {/* <button
              onClick={() => setNewTicketOpen(true)}
              className="flex items-center text-sm bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-full"
            >
              <Plus size={16} className="mr-1" /> New Message
            </button> */}
          </div>

          {contacts.length === 0 ? (
            <div className="text-center py-8">
              <Headset size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No support Message yet</p>
              <button
                onClick={() => setNewTicketOpen(true)}
                className="mt-4 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center mx-auto"
              >
                <Plus size={18} className="mr-2" /> Create First Message
              </button>
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
              {contacts.map((ticket) => (
                <div
                  key={ticket._id}
                  className={`p-3 rounded-lg cursor-pointer transition-all ${selectedContact?._id === ticket._id
                      ? "bg-blue-50 border-l-4 border-blue-500"
                      : "hover:bg-gray-50 border-l-4 border-transparent"
                    }`}
                  onClick={() => {
                    setSelectedContact(ticket);
                    setNewTicketOpen(false);
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium truncate">{ticket.subject}</h3>
                      {/* Show the first message from replies if available */}
                      <p className="text-sm text-gray-600 truncate">
                        {ticket.replies?.[0]?.message || "No message content"}
                        ...
                      </p>
                    </div>
                    <div className="flex flex-col items-end ml-2">
                      <span
                        className={`px-2 py-0.5 text-xs rounded-full ${ticket.status === "replied"
                            ? "bg-green-100 text-green-800"
                            : ticket.status === "new"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                      >
                        {ticket.status}
                      </span>
                      <span className="text-xs text-gray-500 mt-1">
                        {new Date(ticket.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right side - content area */}
        <div className="md:w-2/3">
          {newTicketOpen ? (
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <div className="flex items-center mb-4">
                <Plus size={20} className="text-blue-500 mr-2" />
                <h2 className="text-xl font-semibold">Create New Message</h2>
              </div>
              <form onSubmit={handleCreateNewTicket}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    placeholder="What's this about?"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message
                  </label>
                  <textarea
                    name="message"
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Describe your issue in detail..."
                    required
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setNewTicketOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center"
                  >
                    <Headset size={16} className="mr-2" /> Submit Message
                  </button>
                </div>
              </form>
            </div>
          ) : selectedContact ? (
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <div className="flex justify-between items-center mb-4 pb-4 border-b">
                <div>
                  <h2 className="text-xl font-semibold">
                    {selectedContact.subject}
                  </h2>
                  <div className="flex items-center mt-1">
                    <span
                      className={`px-2 py-0.5 text-xs rounded-full ${selectedContact.status === "replied"
                          ? "bg-green-100 text-green-800"
                          : selectedContact.status === "new"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                    >
                      {selectedContact.status}
                    </span>
                    <span className="text-sm text-gray-500 ml-2">
                      Created: {formatDate(selectedContact.createdAt)}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setNewTicketOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                >
                  <Plus size={18} className="text-white" />
                  <span className="font-medium">New Message</span>
                </button>
              </div>

              {/* Original message */}
              <div className="flex justify-end mb-6">
                <div className="max-w-[80%] bg-blue-50 p-4 rounded-lg rounded-tr-none">
                  <div className="flex items-center justify-end text-xs text-gray-500 mb-2">
                    <span>You on {formatDate(selectedContact.createdAt)}</span>
                    <div className="ml-2 bg-blue-100 p-1 rounded-full">
                      <Headset size={16} className="text-blue-600" />
                    </div>
                  </div>
                  <p className="whitespace-pre-line text-right">
                    {selectedContact.replies[0]?.message ||
                      "No message content"}
                  </p>
                </div>
              </div>

              {/* Conversation replies */}
              {/* <h3 className="font-medium mb-3 text-gray-700 flex items-center">
                <MessageSquare size={18} className="mr-2 text-blue-500" />
                Conversation
              </h3> */}

              {selectedContact.replies?.length > 0 ? (
                <div className="space-y-4">
                  {selectedContact.replies?.map((reply, index) => {
                    // Skip the first reply since it's shown as the original message
                    if (index === 0) return null;

                    return (
                      <div
                        key={index}
                        className={`flex ${reply.sentBy === "admin"
                            ? "justify-start"
                            : "justify-end"
                          }`}
                      >
                        <div
                          className={`max-w-[80%] p-4 rounded-lg ${reply.sentBy === "admin"
                              ? "bg-gray-50 rounded-tl-none"
                              : "bg-blue-50 rounded-tr-none"
                            }`}
                        >
                          <div
                            className={`flex items-center text-xs text-gray-500 mb-2 ${reply.sentBy === "admin"
                                ? "justify-start"
                                : "justify-end"
                              }`}
                          >
                            {reply.sentBy === "admin" && (
                              <div className="mr-2 bg-indigo-100 p-1 rounded-full">
                                <Headset
                                  size={16}
                                  className="text-indigo-600"
                                />
                              </div>
                            )}
                            <span>
                              {reply.sentBy === "admin"
                                ? "Support Team"
                                : "You"}{" "}
                              on {formatDate(reply.sentAt)}
                            </span>
                            {reply.sentBy !== "admin" && (
                              <div className="ml-2 bg-blue-100 p-1 rounded-full">
                                <Headset size={16} className="text-blue-600" />
                              </div>
                            )}
                          </div>
                          <p
                            className={`whitespace-pre-line ${reply.sentBy === "admin"
                                ? "text-left"
                                : "text-right"
                              }`}
                          >
                            {reply.message}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <div className="inline-block p-4 bg-gray-50 rounded-full mb-3">
                    <Clock size={32} />
                  </div>
                  <p>No replies yet from our support team</p>
                  <p className="text-sm mt-1">We'll get back to you soon!</p>
                </div>
              )}

              {/* {selectedContact.replies?.length > 0 ? (
                <div className="space-y-4">
                  {selectedContact.replies?.map((reply, index) => (
                    <div
                      key={index}
                      className={`flex ${
                        reply.sentBy === "admin"
                          ? "justify-start"
                          : "justify-end"
                      }`}
                    >
                      <div
                        className={`max-w-[80%] p-4 rounded-lg ${
                          reply.sentBy === "admin"
                            ? "bg-gray-50 rounded-tl-none"
                            : "bg-blue-50 rounded-tr-none"
                        }`}
                      >
                        <div
                          className={`flex items-center text-xs text-gray-500 mb-2 ${
                            reply.sentBy === "admin"
                              ? "justify-start"
                              : "justify-end"
                          }`}
                        >
                          {reply.sentBy === "admin" && (
                            <div className="mr-2 bg-indigo-100 p-1 rounded-full">
                              <Headset size={16} className="text-indigo-600" />
                            </div>
                          )}
                          <span>
                            {reply.sentBy === "admin" ? "Support Team" : "You"}{" "}
                            on {formatDate(reply.sentAt)}
                          </span>
                          {reply.sentBy !== "admin" && (
                            <div className="ml-2 bg-blue-100 p-1 rounded-full">
                              <Cat size={16} className="text-blue-600" />
                            </div>
                          )}
                        </div>
                        <p
                          className={`whitespace-pre-line ${
                            reply.sentBy === "admin"
                              ? "text-left"
                              : "text-right"
                          }`}
                        >
                          {reply.message}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <div className="inline-block p-4 bg-gray-50 rounded-full mb-3">
                    <Clock size={32} />
                  </div>
                  <p>No replies yet from our support team</p>
                  <p className="text-sm mt-1">We'll get back to you soon!</p>
                </div>
              )} */}

              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Add a reply
                </h4>
                <textarea
                  ref={replyTextareaRef}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 mb-3"
                  placeholder="Type your message here..."
                  value={replyMessage}
                  onChange={(e) => {
                    if (e.target.value.length <= 5000) {
                      // Match your backend limit
                      setReplyMessage(e.target.value);
                    } else {
                      toast.error("Message cannot exceed 5000 characters");
                    }
                  }}
                  maxLength={5000} // HTML validation
                />
                <button
                  onClick={handleSubmitReply}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center ml-auto"
                >
                  <Send size={16} className="mr-2" /> Send Reply
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md p-8 border border-gray-200 text-center">
              <div className="mx-auto w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Headset size={48} className="text-blue-400" />
              </div>
              <h2 className="text-xl font-semibold mb-2">
                Welcome to Support!
              </h2>
              <p className="text-gray-600 mb-6">
                Select a message from the sidebar or create a new one to get
                help from our support team.
              </p>
              <button
                onClick={() => setNewTicketOpen(true)}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center mx-auto"
              >
                <Plus size={18} className="mr-2" /> Create New Message
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserSupport;
